import { forwardRef, useEffect, useImperativeHandle, useMemo } from 'react';
import { DropdownList, Stack } from '@adc/parallax-component-library';
import { useForm, useStore } from '@tanstack/react-form';
import * as z from 'zod';

import { DropdownInput } from '~/components/DropdownInput';
import { buildTestId } from '~/utils/buildTestId';
import {
  DATE_FORMAT_LABELS,
  DATE_FORMAT_VALUES,
  DATE_FORMATS,
  DEFAULT_DATE_FORMAT,
  getDateFormatLabel,
} from '~/utils/dateFormats';
import { getErrorProps } from '~/utils/forms/getErrorProps';

import { departmentGeneralSettingsSchema } from './validations';

export type DepartmentGeneralSettingsFormValues = z.infer<
  typeof departmentGeneralSettingsSchema
>;

export type DepartmentGeneralSettingsFormRef = {
  submitForm: () => Promise<void>;
};

type Props = {
  testID: string;
  onSubmit: (values: DepartmentGeneralSettingsFormValues) => Promise<void>;
  defaultValues?: Partial<DepartmentGeneralSettingsFormValues>;
  hasFormChanged?: (isDirty: boolean) => void;
};

export const DepartmentGeneralSettingsForm = forwardRef<
  DepartmentGeneralSettingsFormRef,
  Props
>(({ testID, onSubmit, defaultValues, hasFormChanged }, ref) => {
  const form = useForm({
    defaultValues: {
      ...defaultValues,
      dateFormat: defaultValues?.dateFormat ?? DEFAULT_DATE_FORMAT,
    },
    validators: { onChange: departmentGeneralSettingsSchema },
    onSubmit: ({ value }) => {
      return onSubmit(departmentGeneralSettingsSchema.parse(value));
    },
  });

  const dateFormatValue = useStore(form.store, (s) => s.values.dateFormat);

  const { selectedIndex, selectedLabel } = useMemo(() => {
    const index = Object.keys(DATE_FORMATS).indexOf(dateFormatValue);
    const label = getDateFormatLabel(dateFormatValue);

    return {
      selectedIndex: index,
      selectedLabel: label,
    };
  }, [dateFormatValue]);

  useImperativeHandle(
    ref,
    () => ({
      submitForm: form.handleSubmit,
    }),
    [form.handleSubmit],
  );

  useEffect(() => {
    if (hasFormChanged) {
      hasFormChanged(form.state.isDirty);
    }
  }, [form.state.isDirty, hasFormChanged]);

  return (
    <Stack gap="$6" paddingTop="$2" testID={testID}>
      <Stack flex={1}>
        <form.Field name="dateFormat">
          {(field) => (
            <DropdownList
              testID={buildTestId({
                testID,
                suffix: ['date', 'format'],
              })}
              items={DATE_FORMAT_LABELS}
              selectedIndex={selectedIndex}
              onSelect={(index) => {
                const newSelectedDateFormat = DATE_FORMAT_VALUES[index];

                field.setValue(newSelectedDateFormat ?? DEFAULT_DATE_FORMAT);
              }}
            >
              <DropdownInput
                testID={buildTestId({
                  testID,
                  suffix: ['input', 'country'],
                })}
                aria-label="Date format"
                label="Date format"
                value={selectedLabel}
                {...getErrorProps(field)}
              />
            </DropdownList>
          )}
        </form.Field>
      </Stack>
    </Stack>
  );
});

DepartmentGeneralSettingsForm.displayName = 'DepartmentGeneralSettingsForm';
